import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Flex, useMediaQuery } from '@chakra-ui/react';
import GuestDropdown from '@view/components/Popovers/GuestDropdown';
import Button from '@molecules/Button';
import Text from '@molecules/Text';
import {
  StyledCardContainer,
  StyledImageWrapper,
  StyledInfoContainer,
  StyledText,
  StyledMark,
} from './StyledRoomBedCard';
import ScreenSize from '../../../constants/screen-sizes';
import { AccomodationUnit } from '@data/models/AccomodationUnit';
import { Accommodation } from '@data/models';
import { useStore } from '@data/other/withStore';
import useFormat from '@business/utils/hooks/useFormat';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import ReservationItem from '@data/models/ReservationItem';
import { ReservationItemAccommodation } from '@data/models/ReservationItem';
import BEImage from '@molecules/BEImage';

interface RoomBedCardProps {
  accommodation: Accommodation;
  unit: AccomodationUnit;
}

const RoomBedCard = ({ accommodation, unit }: RoomBedCardProps) => {
  const {
    images: accomodationImages,
    notDerivedRate,
    rate,
    nights,
    isPrivate,
    maxGuests,
  } = accommodation;
  const { name, description, room_id, images: unitImages } = unit;
  const [isMobile] = useMediaQuery(`(max-width: ${ScreenSize.MOBILE_LARGE})`);

  const {
    property: { cart },
  } = useStore();

  const reservationItem = useMemo((): ReservationItem | undefined => {
    return cart.findAccommodation(accommodation, room_id);
  }, [cart, cart.items, accommodation, room_id]);

  const [adults, setAdults] = useState<number>(Math.min(2, maxGuests));
  const [kids, setKids] = useState<number>(0);

  const { currency } = useFormat();

  const images = useMemo(() => {
    return unitImages.length > 0 ? unitImages : accomodationImages;
  }, [accomodationImages, unitImages]);

  const nightsText = useMemo(() => {
    return nights === 1 ? `${nights} Night` : `${nights} Nights`;
  }, [nights]);

  useEffect(() => {
    if (reservationItem && reservationItem.model) {
      const { adults, kids } =
        reservationItem.model as ReservationItemAccommodation;
      setAdults(adults);
      setKids(kids);
    }
  }, [reservationItem]);

  const onClickAdd = useCallback(() => {
    cart.addAccommodation(accommodation, 1, adults, kids, room_id);
  }, [unit, reservationItem, adults, kids, room_id]);

  const onClickRemove = useCallback(() => {
    cart.removeAccommodation(accommodation, room_id);
  }, [unit, reservationItem, adults, kids, room_id]);

  const onChangeGuests = useCallback((adults, kids) => {
    setAdults(adults);
    setKids(kids);
  }, []);

  return (
    <StyledCardContainer>
      <StyledImageWrapper>
        <BEImage
          width="100%"
          height="100%"
          src={images[0].thumb}
          borderRadius="8px"
        />
      </StyledImageWrapper>
      <StyledInfoContainer>
        <Flex justifyContent="space-between">
          <Flex
            direction="column"
            gap={isMobile ? '0' : '4px'}
            justifyContent="flex-start"
            alignItems="flex-start"
          >
            <Text type="h3">{name}</Text>
            <Text html={description} />
          </Flex>
          <Flex
            direction="column"
            alignItems="flex-end"
            minWidth={isMobile ? '90px' : '150px'}
          >
            <Flex alignItems="center" gap="4px">
              {notDerivedRate > 0 && notDerivedRate !== rate && (
                <Text
                  className="text-decoration"
                  color="error"
                  darkness="400"
                  size="sm"
                  mobilesize="sm"
                >
                  {currency(rate)}
                </Text>
              )}
              <Text type="h3" mobilesize="xl">
                {currency(rate)}
              </Text>
            </Flex>
            <Text
              type="body"
              color="neutral"
              darkness="700"
              size="sm"
              mobilesize="sm"
            >
              {nightsText}
            </Text>
          </Flex>
        </Flex>
        <Flex gap="16px" justifyContent="flex-end" alignItems="end">
          {reservationItem ? (
            <Button
              className="remove-button"
              variant="outline"
              color="black"
              size="md"
              icon="trash-can"
              onClick={onClickRemove}
            >
              Remove
            </Button>
          ) : (
            <>
              {isPrivate && (
                <>
                  <GuestDropdown
                    label="Guests"
                    adults={adults}
                    kids={kids}
                    onChange={onChangeGuests}
                    maxOccupancy={Math.min(
                      accommodation.maxGuests,
                      accommodation.available
                    )}
                    size="md"
                  />
                </>
              )}
              <Button
                className="add-button"
                color="primary"
                size="md"
                onClick={onClickAdd}
              >
                Add
              </Button>
            </>
          )}
        </Flex>
      </StyledInfoContainer>
    </StyledCardContainer>
  );
};

export default observer(RoomBedCard);
